// AI 노트 «단어 바꾸기» 창(클로바 묶음 C3b) — 전사·요약·메모의 같은 글자를 한 번에 바꾼다.
// 🔴 창은 상세 머리(sticky·zIndex 5) 안이 아니라 컴포넌트 루트에 그린다 — 머리 안에 두면
//    쌓임 맥락에 갇혀 하단 재생바(fixed·zIndex 999)에 가린다. 모바일 탭바가 1000 이라 1001 로 올린다.
// 🔸 한 곳씩 바꾸기·n/N·일치 하이라이트는 D 묶음(노트 검색) 몫이다 — 서버 replace 는 «전체» 치환만 있다.
import { useEffect, useRef, useState } from 'react';
import { alertDialog } from '../../components/AppDialog';
import { MEETING_THEME, softWrap } from '../../utils/meetingUtil';
import { addGlossaryTerm, detailOr, replaceWords } from './meetingWordsApi';

const T = MEETING_THEME;

const WORD_MAX = 100;             // 서버 REPLACE_WORD_MAX
const GLOSSARY_TERM_MAX = 50;     // 서버 GLOSSARY_TERM_MAX — 더 길면 단어 등록이 400 이다

const TARGETS = [
  { key: 'transcript', label: '전사' },
  { key: 'summary', label: '요약' },
  { key: 'notes', label: '메모' },
];

const DEFAULT_TARGETS = { transcript: true, summary: true, notes: true };

// initialFind·initialTargets — 노트 검색 줄의 [단어 바꾸기] 가 검색어와 대상(검색에 맞은 종류 ·
// 0건이면 셋 다)을 미리 채운다.
// 🔴 initialFind 는 maxLength 와 같은 WORD_MAX 로 자른다(더 길면 서버가 400 을 낸다).
export default function WordReplaceDialog({ meeting, onClose, onReplaced, initialFind = '', initialTargets }) {
  const [find, setFind] = useState(() => String(initialFind || '').slice(0, WORD_MAX));
  const [replace, setReplace] = useState('');
  const [picked, setPicked] = useState(() => ({ ...DEFAULT_TARGETS, ...(initialTargets || {}) }));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);     // null | { none: true } | { counts, term }
  const [termMsg, setTermMsg] = useState('');
  const [termSaving, setTermSaving] = useState(false);
  const [termDone, setTermDone] = useState(false);
  const findRef = useRef(null);
  const replaceRef = useRef(null);
  const busyRef = useRef(false);

  // 🔴 요청 중에는 닫지 않는다(응답이 와도 받을 곳이 없어진다).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 검색 줄에서 열면 «찾을 단어» 가 이미 채워져 있다 → 첫 포커스는 «바꿀 단어» 로 둔다(열 때 한 번만).
  const prefilled = String(initialFind || '').trim().length > 0;
  useEffect(() => {
    if (prefilled) replaceRef.current?.focus();
  }, []);

  const targetKeys = TARGETS.filter((t) => picked[t.key]).map((t) => t.key);
  const findText = find.trim();
  const replaceText = replace.trim();
  // 서버는 find·replace 를 strip 한 뒤 비교한다(meetings.py:2252-2258) → 같은 기준으로 막는다.
  const canSubmit = !busy && targetKeys.length > 0 && !!findText && findText !== replaceText;

  const clearResult = () => {
    setResult(null);
    setTermMsg('');
    setTermDone(false);
  };

  const submit = async () => {
    if (busyRef.current) return;
    // 🔴 state 가 아니라 입력칸의 현재 값을 읽는다(한글 조합 중에는 state 가 늦다).
    const f = String(findRef.current?.value ?? find).trim();
    const r = String(replaceRef.current?.value ?? replace).trim();
    if (!f || f === r || targetKeys.length === 0) return;
    busyRef.current = true;
    setBusy(true);
    clearResult();
    try {
      const { ok, data } = await replaceWords(meeting.id, { find: f, replace: r, targets: targetKeys });
      if (!ok) {
        alertDialog('단어 바꾸기 실패: ' + detailOr(data, '바꾸지 못했습니다.'));
        return;
      }
      const counts = (data && data.counts) || {};
      const total = (counts.transcript || 0) + (counts.summary || 0) + (counts.notes || 0);
      if (total <= 0) {
        setResult({ none: true });
        return;
      }
      setResult({ counts, term: r });
      if (data && data.meeting && onReplaced) onReplaced(data.meeting);
    } catch (e) {
      alertDialog('단어 바꾸기 실패: 바꾸지 못했습니다.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    // 한글 입력 중의 Enter(조합 확정)는 실행이 아니다 — 조합이 끝나면 keydown 이 한 번 더 온다.
    if (e.nativeEvent?.isComposing) return;
    submit();
  };

  const registerTerm = async () => {
    const term = result?.term || '';
    if (!term || termSaving || termDone) return;
    setTermSaving(true);
    try {
      const { ok, data } = await addGlossaryTerm(term);
      if (!ok) {
        alertDialog('단어 등록 실패: ' + detailOr(data, '등록하지 못했습니다.'));
        return;
      }
      setTermDone(true);
      setTermMsg(data && data.added ? '단어장에 등록했어요.' : '이미 단어장에 있는 단어예요.');
    } catch (e) {
      alertDialog('단어 등록 실패: 등록하지 못했습니다.');
    } finally {
      setTermSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    fontSize: T.font.md,
    padding: '7px 9px',
    borderRadius: T.radius.md,
    border: `1px solid ${T.color.border}`,
  };
  const labelStyle = { display: 'block', fontSize: T.font.sm, fontWeight: 800, color: T.color.muted, marginBottom: '4px' };
  const canRegister = !!result && !result.none && !!result.term && result.term.length <= GLOSSARY_TERM_MAX;

  return (
    <div onClick={() => { if (!busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div role="dialog" aria-modal="true" aria-label="단어 바꾸기" onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '420px', background: '#fff', borderRadius: T.radius.lg, boxShadow: T.shadow.raised, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 12px', borderBottom: `1px solid ${T.color.border}`, background: T.color.subtle }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: T.font.lg, fontWeight: 900, color: T.color.textStrong }}>단어 바꾸기</div>
          <button type="button" title="닫기" aria-label="닫기" onClick={onClose} disabled={busy}
            style={{ border: 'none', background: 'transparent', padding: '2px 4px', color: T.color.muted, cursor: busy ? 'not-allowed' : 'pointer', fontSize: '15px', lineHeight: 1 }}>
            ×
          </button>
        </div>
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <label>
            <span style={labelStyle}>찾을 단어</span>
            <input ref={findRef} autoFocus={!prefilled} aria-label="찾을 단어" maxLength={WORD_MAX} value={find}
              onChange={(e) => { setFind(e.target.value); clearResult(); }} onKeyDown={onKeyDown} style={inputStyle} />
          </label>
          <label>
            <span style={labelStyle}>바꿀 단어</span>
            <input ref={replaceRef} aria-label="바꿀 단어" maxLength={WORD_MAX} value={replace} placeholder="비우면 찾은 단어를 지웁니다"
              onChange={(e) => { setReplace(e.target.value); clearResult(); }} onKeyDown={onKeyDown} style={inputStyle} />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {TARGETS.map((t) => (
              <label key={t.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: T.font.base, fontWeight: 700, color: T.color.text }}>
                <input type="checkbox" checked={!!picked[t.key]}
                  onChange={() => { setPicked((p) => ({ ...p, [t.key]: !p[t.key] })); clearResult(); }} />
                {t.label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: T.font.sm, color: T.color.muted, lineHeight: 1.5, ...softWrap }}>
            대소문자를 구분해 그대로 일치하는 글자를 모두 바꿉니다. 요약이 바뀌면 새 버전으로 남습니다.
          </div>
          <button type="button" onClick={submit} disabled={!canSubmit}
            style={{ alignSelf: 'flex-start', fontSize: T.font.base, fontWeight: 800, padding: '7px 12px', borderRadius: T.radius.md, border: `1px solid ${T.color.outlineBtnBorder}`, background: '#fff', color: T.color.textStrong, cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.55 }}>
            {busy ? '바꾸는 중…' : '전체 바꾸기'}
          </button>
          {result && result.none && (
            <div style={{ fontSize: T.font.base, color: T.color.muted }}>일치하는 단어가 없습니다.</div>
          )}
          {result && !result.none && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '9px 10px', borderRadius: T.radius.md, background: T.color.subtle, border: `1px solid ${T.color.border}` }}>
              <div style={{ fontSize: T.font.base, fontWeight: 800, color: T.color.textStrong, ...softWrap }}>
                {`전사 ${result.counts.transcript || 0}건 · 요약 ${result.counts.summary || 0}건 · 메모 ${result.counts.notes || 0}건을 바꿨어요.`}
              </div>
              {canRegister && (
                <>
                  <div style={{ fontSize: T.font.sm, color: T.color.muted, lineHeight: 1.5, ...softWrap }}>
                    자주 쓰는 단어로 등록하면 인식률이 높아져요.
                  </div>
                  <button type="button" onClick={registerTerm} disabled={termSaving || termDone}
                    style={{ alignSelf: 'flex-start', fontSize: T.font.sm, fontWeight: 800, padding: '5px 10px', borderRadius: T.radius.md, border: `1px solid ${T.color.outlineBtnBorder}`, background: '#fff', color: T.color.textStrong, cursor: (termSaving || termDone) ? 'not-allowed' : 'pointer', opacity: (termSaving || termDone) ? 0.55 : 1 }}>
                    {termSaving ? '등록 중…' : '단어 등록하기'}
                  </button>
                </>
              )}
              {termMsg && <div style={{ fontSize: T.font.sm, color: T.color.muted }}>{termMsg}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
